import React, { useCallback, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { BaseAdminScreen, AdminEmptyScreenLayout, LoadingSpinner } from '@/components';
import AdminErrorState from '@/components/common/admin/mobile/AdminErrorState';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useUserManagement } from '@/hooks/admin/useUserManagement';
import { UserManagementDataTable } from '@/components/admin/userManagement/UserManagementDataTable';
import { IUserManagementTableRow } from '@/types/admin/userManagement';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopUserManagementSidePanel } from '@/components/admin/userManagement/desktop/DesktopUserManagementSidePanel';
import { useErrorToast } from '@/contexts/admin/ToastContext';
import { useErrorToastOnce } from '@/hooks/common/useErrorToastOnce';
import { DataTableProvider } from '@/contexts/admin/DataTableContext';

type UserManagementScreenProps = StackScreenProps<AdminStackParamList, 'UserManagement'>;

const CSV_COLUMNS = ["name", "userType", "email", "organization", "properties"];

export const UserManagementScreen: React.FC<UserManagementScreenProps> = ({
  navigation,
}) => {
  const [csvData, setCsvData] = useState<(string | number | boolean | null)[][]>([CSV_COLUMNS]);
  const { showApiError } = useErrorToast();
  const { layoutMode } = useResponsive();

  // Desktop side panel state
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUserManagementTableRow | undefined>(undefined);

  const {
    data,
    tableRows,
    totalFilteredCount,
    isLoading,
    error,
    enhancedError,
    retryCount,
    canRetry,
    manualRetry,
    paginationModel,
    updatePaginationModel,
    goToNextPage,
    goToPreviousPage,
    hasNextPage,
    hasPreviousPage,
    filterModel,
    updateFilterModel,
    sortModel,
    updateSortModel,
    searchQuery,
    updateSearchQuery,
    refresh,
  } = useUserManagement();

  // Show toast for API errors (guard duplicates)
  useErrorToastOnce(enhancedError, showApiError);
  // Keep UI immediate; no debounce at call site

  // Format CSV data (matching admin-web logic)
  const formatCsvData = useCallback((newData: IUserManagementTableRow[]) => {
    if (!newData || newData.length === 0) {
      setCsvData([CSV_COLUMNS]);
      return;
    }

    const listData = newData.map((row) => [
      row.name,
      row.userType,
      row.email,
      row.organization?.join(', ') || 'No Organization',
      row.properties?.join(', ') || 'No Properties',
    ]);
    setCsvData([CSV_COLUMNS, ...listData]);
  }, []);

  // Handle user selection - navigate to edit user screen
  const selectUserDetails = useCallback((user: IUserManagementTableRow) => {
    if (layoutMode === 'desktop') {
      setSelectedUser(user);
      setShowEditPanel(true);
    } else {
      navigation.navigate('EditUser', { user });
    }
  }, [navigation, layoutMode]);

  // Handle add user functionality
  const handleAddUser = useCallback(() => {
    if (layoutMode === 'desktop') {
      setShowCreatePanel(true);
    } else {
      navigation.navigate('CreateUser');
    }
  }, [navigation, layoutMode]);

  // Determine if we should show data or empty state
  // Show data if we have data, regardless of loading state (prevents blinking)
  const hasData = data.response && data.response.length > 0;
  
  // Show error state if we have a critical error and no data and no search/filter applied
  const hasSearchOrFilter = searchQuery.trim() !== '' || filterModel.items.length > 0;
  const showErrorState = enhancedError && !hasData && !isLoading && !hasSearchOrFilter;
  
  // Show loading state if we're loading and have no data yet and no search/filter applied
  const showLoadingState = isLoading && !hasData && !showErrorState && !hasSearchOrFilter;
  
  // Always show the data table if there's a search/filter applied, even with no results
  const showDataTable = hasData || hasSearchOrFilter;

  // Render error state component
  const renderErrorState = () => (
    <AdminErrorState
      title="Unable to Load Users"
      message={enhancedError?.userFriendlyMessage || 'Something went wrong while loading the user management list.'}
      retryCount={retryCount}
      canRetry={canRetry}
      onRetry={manualRetry}
      retryButtonText="Retry"
    />
  );

  // Desktop layout
  if (layoutMode === 'desktop') {
    return (
      <AdminErrorBoundary context="UserManagement">
        <DesktopAdminLayout>
          <DesktopPageHeader
            title="Admin User Management"
            subtitle=""
            showAddButton={true}
            addButtonText="New User"
            onAddPress={handleAddUser}
            showExportButton={false}
          />
          {showLoadingState ? (
            <LoadingSpinner message="Loading users..." />
          ) : showErrorState ? (
            renderErrorState()
          ) : (
            <DataTableProvider tableKey="admin:userManagement" defaults={{ paginationModel }}>
              <View style={styles.dataContainer}>
                <UserManagementDataTable
                  data={data}
                  tableRows={tableRows}
                  totalFilteredCount={totalFilteredCount}
                  isLoading={isLoading}
                  onUserSelect={selectUserDetails}
                  updateCsvData={formatCsvData}
                  showSearch={false}
                />
              </View>
            </DataTableProvider>
          )}

          {/* Side Panels */}
          {showCreatePanel && (
            <DesktopUserManagementSidePanel
              visible={true}
              onClose={() => setShowCreatePanel(false)}
              mode="create"
            />
          )}
          {showEditPanel && (
            <DesktopUserManagementSidePanel
              visible={true}
              onClose={() => setShowEditPanel(false)}
              mode="edit"
              user={selectedUser}
            />
          )}
        </DesktopAdminLayout>
      </AdminErrorBoundary>
    );
  }

  // Mobile layout (existing)
  return (
    <AdminErrorBoundary context="UserManagement">
      <BaseAdminScreen
        screenName="UserManagement"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <AdminEmptyScreenLayout
          title="User Management"
          emptyStateType="userManagement"
          onAddPress={handleAddUser}
          showAddButton={true}
          buttonType="add"
          hasContent={showDataTable || showErrorState || showLoadingState}
        >
          {showLoadingState ? (
            <LoadingSpinner message="Loading users..." />
          ) : showErrorState ? (
            renderErrorState()
          ) : showDataTable ? (
            <DataTableProvider tableKey="admin:userManagement" defaults={{ paginationModel }}>
              <View style={styles.dataContainer}>
                <UserManagementDataTable
                  data={data}
                  tableRows={tableRows}
                  totalFilteredCount={totalFilteredCount}
                  isLoading={isLoading}
                  onUserSelect={selectUserDetails}
                  updateCsvData={formatCsvData}
                  showSearch={false}
                />
              </View>
            </DataTableProvider>
          ) : null}
        </AdminEmptyScreenLayout>
      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  dataContainer: {
    flex: 1,
    width: '100%',
  },
});
